// pages/UserAdmin/crm/TabelaContatos.js — a lista de contatos (out/26).
//
// Do Claude Design "CRM Moderno.dc.html": cartão de cantos 18 com o
// cabeçalho preso no topo, a letra do alfabeto como divisória (Syne verde +
// fio, em vez da faixa cinza de largura toda), avatar com a cor do contato,
// agente e interesse editáveis no clique (sem abrir o contato — numa
// triagem de 479 contatos, abrir e fechar o modal por linha era o gargalo)
// e o "Situação" sempre presente como coluna (vazia com "—"), pra o número
// de células por linha ser constante.
//
// Abaixo de 1180px as colunas Agente e Situação saem: o nome nunca quebra
// em três linhas pra caber as outras.
//
// Ao recarregar, as linhas antigas ficam na tela e uma barra verde de 2px
// corre no topo do cartão; só o primeiro carregamento (lista vazia) mostra
// linhas-esqueleto.

import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { FiCheck, FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SilenceBadge, { isSilent } from './SilenceBadge';
import SeloQualificacao from '../../../components/qualificacao/SeloQualificacao';
import { CRM_THEME, INTEREST_LABELS, OUTFIT, SYNE, UNSET, avatarDe, formatarFone, initials } from './theme';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const ESTREITA = '@media (max-width: 1179.98px)';
// No celular o número desce pra baixo do nome: três colunas lado a lado em
// 390px deixavam "Academia …" e "55219660…" cortados ao mesmo tempo.
const CELULAR = '@media (max-width: 599.98px)';

// Colunas num lugar só — cabeçalho, faixa de alcance e linhas usam a mesma.
export const colunas = (isSupervisor) => ({
  gridTemplateColumns: isSupervisor
    ? '40px minmax(0,2fr) minmax(150px,1.25fr) minmax(130px,1.15fr) minmax(160px,1.05fr) minmax(130px,0.9fr)'
    : '40px minmax(0,2fr) minmax(150px,1.25fr) minmax(160px,1.05fr) minmax(130px,0.9fr)',
  [ESTREITA]: { gridTemplateColumns: '36px minmax(0,1.7fr) minmax(0,1.2fr) minmax(0,1fr)' },
  [CELULAR]: { gridTemplateColumns: '32px minmax(0,1fr) auto' },
});
const soLarga = { display: 'flex', [ESTREITA]: { display: 'none' } };
const foraDoCelular = { [CELULAR]: { display: 'none' } };

export function Caixinha({ marcado, parcial }) {
  const cheia = marcado || parcial;
  return (
    <Box component="span" sx={{
      width: 18, height: 18, borderRadius: '5px', flexShrink: 0,
      border: `1.5px solid ${cheia ? 'var(--ch-green)' : 'var(--ch-bdr2)'}`, bgcolor: cheia ? 'var(--ch-green)' : 'var(--ch-card)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cx-on-green)',
      transition: 'background-color .12s, border-color .12s',
    }}>
      {marcado ? <FiCheck size={12} strokeWidth={3.4} /> : parcial ? <Box component="span" sx={{ width: 8, height: 2, borderRadius: '1px', bgcolor: 'var(--cx-on-green)' }} /> : null}
    </Box>
  );
}

export function PilulaInteresse({ nivel, onClick, alto = 25 }) {
  const k = nivel && CRM_THEME.interest[nivel] ? nivel : UNSET;
  const t = CRM_THEME.interest[k];
  return (
    <Box component={onClick ? 'button' : 'span'} type={onClick ? 'button' : undefined} onClick={onClick}
      title={onClick ? 'Mudar nível de interesse' : undefined}
      sx={{
        height: alto, display: 'inline-flex', alignItems: 'center', gap: '6px', px: '10px', borderRadius: '999px',
        bgcolor: t.bg, color: t.color, border: `1px ${t.dashed ? 'dashed' : 'solid'} ${t.border}`,
        fontFamily: OUTFIT, fontSize: alto > 25 ? 13 : 12.5, fontWeight: 600, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default', transition: 'transform .12s',
        ...(onClick ? { '&:hover': { transform: 'translateY(-1px)' }, '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } } : {}),
        '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
      }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: t.dashed ? 'transparent' : t.dot, border: t.dashed ? '1.5px dashed var(--cx-cap)' : 0 }} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{INTEREST_LABELS[k]}</Box>
    </Box>
  );
}

// Out/26 (rastreamento PR 1): o contato chegou por um anúncio "Clique para o
// WhatsApp". Selo pequeno e roxo (a cor de "origem" em todo o CRM), com o
// título do anúncio no title — a janela do contato mostra o resto.
export function SeloAnuncio({ origem }) {
  const onde = origem.rede === 'instagram' ? ' no Instagram' : origem.rede === 'facebook' ? ' no Facebook' : '';
  const dica = origem.titulo ? `Veio do anúncio${onde}: “${origem.titulo}”` : `Veio de um anúncio${onde}`;
  return (
    <Box component="span" title={dica} aria-label={dica} sx={{
      flexShrink: 0, height: 20, px: '7px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center',
      bgcolor: 'var(--ch-purple-bg)', color: 'var(--ch-purple-d)', border: '1px solid var(--ch-purple-b)',
      fontFamily: OUTFIT, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      Anúncio
    </Box>
  );
}

// A barra verde do topo: 0 → 85% enquanto carrega, 100% e some quando chega.
function BarraProgresso({ carregando }) {
  const [fase, setFase] = useState(null);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (carregando) {
      setFase('inicio');
      t.current = setTimeout(() => setFase('corre'), 20);
    } else {
      setFase((f) => (f ? 'fim' : null));
      t.current = setTimeout(() => setFase(null), 450);
    }
    return () => clearTimeout(t.current);
  }, [carregando]);
  const largura = fase === 'corre' ? '85%' : fase === 'fim' ? '100%' : '0%';
  return (
    <Box aria-hidden sx={{
      position: 'absolute', top: 0, left: 0, height: 2, zIndex: 7, bgcolor: 'var(--ch-green)', width: largura,
      opacity: fase && fase !== 'fim' ? 1 : 0,
      transition: fase === 'corre' ? 'width .9s cubic-bezier(.2,.7,.3,1)' : fase === 'fim' ? 'width .15s ease, opacity .3s ease .1s' : 'none',
    }} />
  );
}

const CABECALHOS = [
  { key: 'name', label: 'Nome', sort: 'name' },
  { key: 'phone', label: 'Número', sort: 'phone', celular: false },
  { key: 'agent', label: 'Agente', larga: true, supervisor: true },
  // "Situação" mostra `days_since_last_inbound`, que não é coluna do banco:
  // quem ordena no backend é `sort=silence`. asc = quem está calado há mais
  // tempo primeiro, a ordem útil pra triagem.
  { key: 'situacao', label: 'Situação', larga: true, sort: 'silence', titulo: 'Ordenar por tempo sem resposta' },
  { key: 'interest', label: 'Interesse', sort: 'interest' },
];

export default function TabelaContatos({
  items, total, totalAll, page, totalPages, perPage, loading, isSupervisor,
  selectedId, selectedIds, paginaToda, onToggleAll, onToggle, onOpen,
  sort, sortDir, onSort, onAgentMenu, onInterestMenu, escopo, onPrev, onNext,
}) {
  const cols = colunas(isSupervisor);
  const algum = selectedIds.size > 0;
  const letras = sort === 'name' && sortDir === 'asc';
  const tecla = (fn) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); fn(e); } };

  return (
    <Box component="section" aria-label="Lista de contatos" sx={{
      mt: '14px', flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
      bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-bdr)', borderRadius: '18px', boxShadow: 'var(--cx-shadow)',
      '@media (max-width: 599.98px)': { minHeight: 420 },
    }}>
      <BarraProgresso carregando={loading} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'var(--ch-bdr2) transparent' }}>
        {/* Cabeçalho e faixa de alcance grudam juntos no topo. */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 5 }}>
          <Box sx={{ display: 'grid', ...cols, alignItems: 'center', height: 38, px: '20px', bgcolor: 'var(--ch-surf)', borderBottom: '1px solid var(--ch-bdr)' }}>
            {/* Pintado à mão pra bater com o visual — os papéis de
                acessibilidade vão explícitos: a multi-seleção é o único
                caminho pras ações em lote. */}
            <Box component="span" role="checkbox" tabIndex={0}
              aria-checked={paginaToda ? true : algum ? 'mixed' : false}
              aria-label="Selecionar todos os contatos desta página"
              title={paginaToda ? 'Desmarcar todos' : 'Selecionar esta página'}
              onClick={onToggleAll} onKeyDown={tecla(onToggleAll)}
              sx={{ display: 'flex', alignItems: 'center', height: '100%', cursor: 'pointer', '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2, borderRadius: '4px' } }}>
              <Caixinha marcado={paginaToda} parcial={algum && !paginaToda} />
            </Box>
            {CABECALHOS.filter((h) => !h.supervisor || isSupervisor).map((h) => {
              const ativo = h.sort && sort === h.sort;
              const conteudo = (<>{h.label}<Box component="span" sx={{ fontSize: 13, letterSpacing: 0 }}>{ativo ? (sortDir === 'asc' ? '↑' : '↓') : ''}</Box></>);
              const base = {
                alignItems: 'center', gap: '6px', fontFamily: OUTFIT, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: ativo ? 'var(--ch-text)' : 'var(--cx-cap)', whiteSpace: 'nowrap', userSelect: 'none',
                ...(h.larga ? soLarga : { display: 'flex' }),
                ...(h.celular === false ? foraDoCelular : {}),
              };
              return h.sort ? (
                <Box key={h.key} component="button" type="button" onClick={() => onSort(h.sort)} title={h.titulo}
                  sx={{ ...base, border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', justifySelf: 'start', '&:hover': { color: 'var(--ch-text)' }, '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } }}>
                  {conteudo}
                </Box>
              ) : <Box key={h.key} component="span" sx={base}>{h.label}</Box>;
            })}
          </Box>
          {escopo}
        </Box>

        {loading && items.length === 0 ? (
          Array.from({ length: 8 }, (_, i) => (
            <Box key={i} aria-hidden sx={{ display: 'grid', ...cols, alignItems: 'center', minHeight: 48, px: '20px', borderBottom: '1px solid var(--ch-bdr)' }}>
              <span />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'var(--ch-inset)' }} />
                <Box sx={{ height: 10, width: `${45 + ((i * 13) % 35)}%`, borderRadius: '999px', bgcolor: 'var(--ch-inset)' }} />
              </Box>
              <Box sx={{ height: 10, width: '62%', borderRadius: '999px', bgcolor: 'var(--ch-inset)' }} />
            </Box>
          ))
        ) : items.length === 0 ? (
          <Box sx={{ p: '64px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center' }}>
            <Box component="span" sx={{ fontFamily: SYNE, fontSize: 18, fontWeight: 800, color: 'var(--ch-text)' }}>Nenhum contato encontrado.</Box>
            <Box component="span" sx={{ fontSize: 14.5, color: 'var(--cx-cap)' }}>
              {totalAll === 0 ? 'Crie o primeiro em “Novo contato” ou importe uma planilha.' : 'Ajuste a busca ou o filtro.'}
            </Box>
          </Box>
        ) : items.map((c, idx) => {
          const L = initials(c.name);
          const mostraLetra = letras && (idx === 0 || initials(items[idx - 1].name) !== L);
          const av = avatarDe(c.id);
          const aberto = selectedId === c.id;
          const marcado = selectedIds.has(c.id);
          const ag = c.assigned_user_id ? avatarDe(c.assigned_user_id) : null;
          const abrir = () => onOpen(c.id);
          return (
            <Box key={c.id}>
              {mostraLetra && (
                <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '9px 20px 3px 60px', [ESTREITA]: { pl: '56px' } }}>
                  <Box component="span" sx={{ fontFamily: SYNE, fontSize: 12, fontWeight: 800, color: 'var(--ch-green-d)', lineHeight: 1 }}>{L}</Box>
                  <Box component="span" sx={{ flex: 1, height: '1px', bgcolor: 'var(--ch-bdr)' }} />
                </Box>
              )}
              <Box role="button" tabIndex={0} aria-label={`Abrir ${c.name || c.phone_number}`}
                onClick={abrir}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); abrir(); } }}
                sx={{
                  display: 'grid', ...cols, alignItems: 'center', minHeight: 48, px: '20px', borderBottom: '1px solid var(--ch-bdr)',
                  bgcolor: aberto ? 'var(--ch-green-bg)' : marcado ? 'var(--cx-sel)' : 'transparent',
                  boxShadow: aberto ? 'inset 3px 0 0 var(--ch-green)' : 'none', cursor: 'pointer', transition: 'background-color .12s',
                  '&:hover': { bgcolor: aberto ? 'var(--ch-green-bg)' : 'var(--cx-hover)' },
                  '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: -2 },
                }}>
                <Box component="span" role="checkbox" tabIndex={0} aria-checked={marcado} aria-label={`Selecionar ${c.name || c.phone_number}`}
                  onClick={(e) => { e.stopPropagation(); onToggle(c.id); }} onKeyDown={tecla(() => onToggle(c.id))}
                  sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', cursor: 'pointer', '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2, borderRadius: '4px' } }}>
                  <Caixinha marcado={marcado} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <Box component="span" aria-hidden sx={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: av.bg, color: av.fg, fontFamily: SYNE, fontWeight: 800, fontSize: 12 }}>{L}</Box>
                  {/* Uma linha só, com reticências: nome longo quebrando
                      fazia a linha crescer e desalinhava a leitura. */}
                  <Box component="span" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box component="span" sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                      <Box component="span" title={c.name} sx={{ minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--ch-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</Box>
                      {c.origem_anuncio && <SeloAnuncio origem={c.origem_anuncio} />}
                      {/* Qualificação (rastreamento PR 2): só o ícone na lista — a
                          frase (com motivo ou valor) fica no title. */}
                      {c.qualificacao && <SeloQualificacao qualificacao={c.qualificacao} compacto />}
                    </Box>
                    <Box component="span" sx={{ display: 'none', [CELULAR]: { display: 'block' }, fontSize: 12.5, color: 'var(--ch-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
                      {formatarFone(c.phone_number)}
                    </Box>
                  </Box>
                </Box>
                <Box component="span" sx={{ fontSize: 13.5, color: 'var(--ch-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums', pr: '8px', ...foraDoCelular }}>
                  {formatarFone(c.phone_number)}
                </Box>
                {isSupervisor && (
                  <Box sx={{ ...soLarga, minWidth: 0, pr: '8px' }}>
                    <Box component="button" type="button" title="Atribuir a um agente" aria-haspopup="menu"
                      onClick={(e) => { e.stopPropagation(); onAgentMenu(e, c); }}
                      sx={{
                        maxWidth: '100%', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: '8px', ml: '-8px', p: '4px 8px',
                        borderRadius: '9px', border: '1px solid transparent', bgcolor: 'transparent', cursor: 'pointer', fontFamily: OUTFIT,
                        transition: 'background-color .12s, border-color .12s',
                        '&:hover': { bgcolor: 'var(--ch-inset)', borderColor: 'var(--ch-bdr)' },
                        '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
                      }}>
                      <Box component="span" aria-hidden sx={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: ag ? ag.bg : 'transparent', color: ag ? ag.fg : 'var(--cx-cap)', border: ag ? 0 : '1.5px dashed var(--ch-bdr2)',
                        fontFamily: SYNE, fontSize: 10, fontWeight: 800,
                      }}>{ag ? initials(c.assigned_user_name) : ''}</Box>
                      <Box component="span" sx={{ minWidth: 0, fontSize: 13, fontWeight: ag ? 500 : 400, color: ag ? 'var(--ch-text)' : 'var(--cx-cap)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.assigned_user_name || 'Sem responsável'}
                      </Box>
                      <Box component="span" sx={{ display: 'flex', flexShrink: 0, color: 'var(--cx-cap)', opacity: 0.6 }}><FiChevronDown size={12} /></Box>
                    </Box>
                  </Box>
                )}
                <Box sx={{ ...soLarga, alignItems: 'center', minWidth: 0 }}>
                  {isSilent(c) ? <SilenceBadge days={c.days_since_last_inbound} /> : <Box component="span" sx={{ color: 'var(--ch-text3)', fontSize: 13 }}>—</Box>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <PilulaInteresse nivel={c.interest_status} onClick={(e) => { e.stopPropagation(); onInterestMenu(e, c); }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {total > 0 && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '8px 14px 8px 20px', borderTop: '1px solid var(--ch-bdr)', bgcolor: 'var(--ch-surf)' }}>
          <Box component="span" sx={{ fontSize: 13, color: 'var(--ch-text2)', fontVariantNumeric: 'tabular-nums' }}>
            Mostrando <Box component="b" sx={{ fontWeight: 600, color: 'var(--ch-text)' }}>{fmt((page - 1) * perPage + 1)}–{fmt(Math.min(page * perPage, total))}</Box> de <Box component="b" sx={{ fontWeight: 600, color: 'var(--ch-text)' }}>{fmt(total)}</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {[['Página anterior', <FiChevronLeft size={15} />, page <= 1, onPrev], ['Próxima página', <FiChevronRight size={15} />, page >= totalPages, onNext]].map(([rot, ic, off, fn], i) => (
              <Box key={rot} sx={{ display: 'contents' }}>
                {i === 1 && (
                  <Box component="span" sx={{ px: '8px', fontSize: 13, fontWeight: 600, color: 'var(--ch-text)', fontVariantNumeric: 'tabular-nums' }}>{page} / {totalPages}</Box>
                )}
                <Box component="button" type="button" aria-label={rot} disabled={off} onClick={fn}
                  sx={{
                    width: 30, height: 30, p: 0, borderRadius: '9px', border: '1px solid var(--ch-bdr)', bgcolor: 'var(--ch-card)', color: 'var(--ch-text2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    '&:hover:not(:disabled)': { borderColor: 'var(--ch-bdr2)', color: 'var(--ch-text)' },
                    '&:disabled': { opacity: 0.45, cursor: 'default' },
                    '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
                  }}>{ic}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
